import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, FileButton, Grid, Table } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button, EmptyMessage, Loader, Pagination, TextInput } from "components";
import { IRootState } from "config/redux";
import { ServiceConstants } from "constant";
import { useListHook } from "hooks";
import { TuttiService } from "services";
import { tableClasses } from "styles";
import { TuttiClientResponse } from "types";
import { AppUtils } from "utils";

const { REQUEST_PENDING, REQUEST_TUTTI_BATCH_UPLOAD_ID, REQUEST_TUTTI_CLIENTS_ID } =
  ServiceConstants;
const EXCEL_ACCEPT =
  ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

let searchTimeout: NodeJS.Timeout | undefined;

interface TuttiClientsListProps {
  canViewTutti?: boolean;
  showUploadButton?: boolean;
}

const TuttiClientsList = ({
  canViewTutti = true,
  showUploadButton = false,
}: TuttiClientsListProps) => {
  const { t } = useTranslation();
  const serviceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_TUTTI_CLIENTS_ID],
  );
  const uploadServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_TUTTI_BATCH_UPLOAD_ID],
  );
  const { page, content, updatePageable, resetList, totalPages, refreshList, setPage } =
    useListHook();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const searchInitialized = useRef(false);
  const resetUploadRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    resetList();
  }, [resetList]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_TUTTI_CLIENTS_ID, (val) => {
      updatePageable(val);
    });
  }, [serviceObject, updatePageable]);

  useEffect(() => {
    if (!canViewTutti) {
      resetList();
      return;
    }

    if (page === undefined) {
      return;
    }
    if (page === -1) {
      resetList();
      return;
    }

    TuttiService.tuttiClients({
      page,
      pageSize: 20,
      search: debouncedSearch,
    });
  }, [canViewTutti, debouncedSearch, page, resetList]);

  useEffect(() => {
    if (!canViewTutti) {
      return;
    }

    if (!searchInitialized.current) {
      searchInitialized.current = true;
      return;
    }
    if (debouncedSearch === undefined) {
      return;
    }
    const length = debouncedSearch.trim().length;
    if (length > 0 && length < 3) {
      return;
    }
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(refreshList, 500);
  }, [canViewTutti, debouncedSearch, refreshList]);

  const isLoading = useMemo(
    () => serviceObject && serviceObject.status === REQUEST_PENDING,
    [serviceObject],
  );
  const isUploading = useMemo(
    () => uploadServiceObject && uploadServiceObject.status === REQUEST_PENDING,
    [uploadServiceObject],
  );

  const handleUpload = useCallback(async (file: File | null) => {
    resetUploadRef.current?.();

    if (!file) {
      return;
    }

    const response = await TuttiService.tuttiUploadBatch(file);

    if (!response) {
      notifications.show({
        color: "red",
        message: t("clients.tutti.notifications.uploadErrorMessage"),
        title: t("clients.tutti.notifications.uploadErrorTitle"),
      });
      return;
    }

    notifications.show({
      color: "teal",
      message: t("clients.tutti.notifications.uploadSuccessMessage", {
        duplicateRows: response.duplicateRows,
        importedRows: response.importedRows,
        invalidRows: response.invalidRows,
        totalRows: response.totalRows,
      }),
      title: t("clients.tutti.notifications.uploadSuccessTitle"),
    });

    refreshList();
  }, [refreshList, t]);

  const rows = useMemo(() => {
    if (!content) {
      return <></>;
    }

    return content.map((item: TuttiClientResponse) => (
      <Table.Tr key={item.id}>
        <Table.Td>{item.firstName}</Table.Td>
        <Table.Td>{item.lastName}</Table.Td>
        <Table.Td visibleFrom={"md"}>{item.gender ? item.gender.charAt(0) : undefined}</Table.Td>
        <Table.Td>{[item.countryCode, item.phoneNumber].filter(Boolean).join(" ")}</Table.Td>
        <Table.Td visibleFrom={"md"}>{item.email}</Table.Td>
        <Table.Td visibleFrom={"md"}>{item.city}</Table.Td>
        <Table.Td>{item.source ? t(`source.options.${item.source}`) : undefined}</Table.Td>
        {item.lastActivity ? (
          <>
            <Table.Td visibleFrom={"md"}>
              {t("format.time", { date: new Date(item.lastActivity) })}
            </Table.Td>
            <Table.Td visibleFrom={"md"}>
              {t("format.date", { date: new Date(item.lastActivity) })}
            </Table.Td>
          </>
        ) : item.info?.createdAt ? (
          <>
            <Table.Td visibleFrom={"md"}>
              {t("format.time", { date: new Date(item.info.createdAt) })}
            </Table.Td>
            <Table.Td visibleFrom={"md"}>
              {t("format.date", { date: new Date(item.info.createdAt) })}
            </Table.Td>
          </>
        ) : (
          <Table.Td visibleFrom={"md"} colSpan={2} />
        )}
      </Table.Tr>
    ));
  }, [content, t]);

  if (!canViewTutti) {
    return <EmptyMessage message={t("authority.no")} />;
  }

  return (
    <Box key="list-tutti">
      <Grid justify={"flex-end"}>
        {showUploadButton && (
          <Grid.Col span={{ sm: 1, md: 4, lg: 8 }}>
            <FileButton
              accept={EXCEL_ACCEPT}
              disabled={isUploading}
              onChange={handleUpload}
              resetRef={resetUploadRef}
            >
              {(props) => (
                <Button {...props} disabled={isUploading} size={"sm"}>
                  <IconPlus />
                </Button>
              )}
            </FileButton>
          </Grid.Col>
        )}
        <Grid.Col span={{ sm: showUploadButton ? 11 : 12, md: 8, lg: 4 }}>
          <TextInput
            placeholder={t("search.clients")}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </Grid.Col>
      </Grid>
      <Box pos={"relative"}>
        <Loader overlay={true} visible={content === undefined || isLoading || isUploading} />
        {content &&
          (content.length === 0 ? (
            <EmptyMessage message={t("clients.list.empty")} />
          ) : (
            <>
              <Table className={tableClasses.table}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("user.firstName.label")}</Table.Th>
                    <Table.Th>{t("user.lastName.label")}</Table.Th>
                    <Table.Th visibleFrom={"md"}>{t("user.gender.label")}</Table.Th>
                    <Table.Th>{t("user.phone.label")}</Table.Th>
                    <Table.Th visibleFrom={"md"}>{t("user.email.label")}</Table.Th>
                    <Table.Th visibleFrom={"md"}>{t("user.address.city.label")}</Table.Th>
                    <Table.Th>{t("source.title")}</Table.Th>
                    <Table.Th visibleFrom={"md"} colSpan={2}>
                      {t("clients.date.change.label")}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </>
          ))}
      </Box>
    </Box>
  );
};

export default TuttiClientsList;
